import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import ServiceRequestForm from "./serviceRequest/ServiceRequestForm";

/**
 * Modal for requesting a "Beyond the Sale" service.
 * State + submit lives here; the form body (toggles + text inputs) is delegated
 * to ServiceRequestForm so this component only handles orchestration.
 */
export default function RequestServiceModal({ open, onClose, initialService }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [selected, setSelected] = useState([]);
  const [brand, setBrand] = useState("");
  const [modelName, setModelName] = useState("");
  const [ref, setRef] = useState("");
  const [notes, setNotes] = useState("");
  const [photo, setPhoto] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected(initialService ? [initialService] : []);
      setBrand(""); setModelName(""); setRef(""); setNotes(""); setPhoto("");
    }
  }, [open, initialService]);

  if (!open) return null;

  const toggle = (s) => setSelected((cur) =>
    cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { onClose?.(); nav("/login"); return; }
    if (selected.length === 0) return toast.error("Pick at least one service");
    if (!brand.trim() || !modelName.trim()) return toast.error("Brand and Model are required");
    if (!photo) return toast.error("Please upload a photo of the watch so we can see its condition");
    setBusy(true);
    try {
      await api.post("/service-requests", {
        services: selected,
        brand: brand.trim(),
        model_name: modelName.trim(),
        reference_number: ref.trim(),
        notes: notes.trim(),
        photo_data_url: photo,
      });
      toast.success("Request received. Our atelier will send you a quote shortly.");
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit request");
    }
    setBusy(false);
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="request-service-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-md glass-strong rounded-lg p-5 border border-white/10 max-h-[92vh] overflow-y-auto"
      >
        <div className="overline text-gold mb-0.5 text-[10px]">Beyond the Sale</div>
        <h3 className="font-serif text-xl mb-3" data-testid="request-service-title">Request atelier service</h3>

        <ServiceRequestForm
          selected={selected} onToggle={toggle}
          brand={brand} setBrand={setBrand}
          modelName={modelName} setModelName={setModelName}
          ref={ref} setRef={setRef}
          notes={notes} setNotes={setNotes}
          photo={photo} setPhoto={setPhoto}
        />

        <p className="text-[10px] text-white/50 mb-3 leading-snug">
          Our atelier will review your piece and send you a quote in-app. You can accept or decline before any payment is taken.
        </p>

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-[11px] uppercase tracking-widest border border-white/20 hover:border-white/40 rounded"
            data-testid="sr-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-4 py-1.5 text-[11px] uppercase tracking-widest bg-gold text-black hover:bg-gold-hover disabled:opacity-50 rounded"
            data-testid="sr-submit"
          >
            {busy ? "Sending…" : (user ? "Submit request" : "Sign in to submit")}
          </button>
        </div>
      </form>
    </div>
  );
}
